import { Alert, Button, Drawer, Skeleton, Typography } from 'antd'
import {
  WORKING_UNIT_PRESSURE,
  WORKING_UNIT_THICKNESS,
} from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { FormulaDisplay } from 'features/libra/components/FormulaDisplay'
import {
  InputProvenance,
  InputProvenanceRow,
} from 'features/libra/components/InputProvenanceRow'
import { computeMinThicknessValidation } from 'features/libra/stepComponents/ffs/computeMinThicknessValidation'
import { FfsAssetModelEditor } from 'features/libra/stepComponents/ffs/FfsAssetModelEditor'
import { useFfsInputProvenance } from 'features/libra/stepComponents/ffs/useFfsInputProvenance'
import { useFfsTicketContext } from 'features/libra/stepComponents/ffs/useFfsTicketContext'
import { useObservedMinThickness } from 'features/libra/stepComponents/ffs/useObservedMinThickness'
import {
  buildMinThicknessFormulaJsx,
  fmt,
} from 'features/libra/stepComponents/FfsMinimumThicknessFormulas'
import { ObservedMinThicknessCheckPanel } from 'features/libra/stepComponents/ObservedMinThicknessCheckPanel'
import { useReportValidation } from 'features/libra/StepValidationContext'
import { useActiveScopedFieldAccess } from 'features/libra/ticket/useScopedFieldAccess'
import {
  LibraWorflowStepComponentProps,
  StepValidationResult,
} from 'features/libra/workflow/workflowStepsApi'
import { FC, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0;
`

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-bottom: 1rem;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  }
`

const SectionTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.forecast.activeText};
`

/**
 * Renders the API 579 piping `t_min` and `MAWP` calculations for the
 * (component, inspection) pair. The step is read-only -- all inputs flow
 * from earlier steps (Asset Model for D / t_nom; Data Gathering for P,
 * S_y, E, Y, t_sl). Every input row exposes provenance so the engineer
 * can jump back to the cited document or to the Asset Model step. Every
 * formula shows symbolic, substituted, and computed result.
 *
 * The observed-minimum check at the bottom compares t_min against the
 * minimum reading in the inspection's thickness CDF (sourced from shared
 * `useQuantitativeDataSummaryStats` -- same stats the viz panel uses).
 */
export const Component: FC<
  LibraWorflowStepComponentProps<Record<string, never>>
> = () => {
  const {
    component,
    componentId,
    inspectionId,
    unit,
    assetModel,
    componentModel,
    isLoading,
    forceRefresh: refreshTicketContext,
  } = useFfsTicketContext()
  // Legacy fallback only: pre-cutover tickets stored the operating params in
  // scoped `fieldValues`. New tickets read everything from the asset-model
  // snapshot (`componentModel`); `computeMinThicknessValidation` prefers the
  // snapshot and falls back to these.
  const { fieldValues } = useActiveScopedFieldAccess()
  // Citations for the asset-model inputs live on the ticket's annotation-only
  // `inputProvenance` overlay. This step DISPLAYS them read-only (keyed by stable
  // field path); editing sources happens in the Asset Model step's editor.
  const { getFieldSource } = useFfsInputProvenance(componentId)
  const {
    validation: baseValidation,
    inputs: { raw },
    results,
    missingInputs,
  } = useMemo(
    () => computeMinThicknessValidation({ componentModel, fieldValues }),
    [componentModel, fieldValues]
  )

  const tMinNumForCheck = results.tMin?.toNumber(WORKING_UNIT_THICKNESS) ?? null

  const {
    observedMinMm,
    isLoading: observedLoading,
    noCdf,
  } = useObservedMinThickness({ componentId, inspectionId })

  const cdsCheckPass: boolean | null =
    observedMinMm !== null && tMinNumForCheck !== null
      ? observedMinMm >= tMinNumForCheck
      : null

  // Compose the base (missing-inputs) validation with the observed-CDS check.
  // Error wins over Warning; loading wins over a stale undefined result so
  // the Next button can't activate before the observed-min check resolves.
  const finalValidation = useMemo<StepValidationResult>(() => {
    if (baseValidation?.status === 'Error') return baseValidation
    if (observedLoading && tMinNumForCheck !== null) {
      return {
        status: 'Warning',
        message: 'Loading observed minimum thickness check...',
      }
    }
    if (noCdf && tMinNumForCheck !== null) {
      return {
        status: 'Warning',
        message:
          'No thickness CDF for this inspection -- observed minimum check cannot run.',
      }
    }
    if (cdsCheckPass === false) {
      return {
        status: 'Warning',
        message:
          'Observed minimum thickness in the CDS is below the required t_min. Inspect the failing region.',
      }
    }
    return baseValidation
  }, [baseValidation, cdsCheckPass, noCdf, observedLoading, tMinNumForCheck])

  useReportValidation(finalValidation)

  // Asset Model edit drawer -- opened from any InputProvenanceRow with
  // `kind: 'assetModel'`. Engineer fills missing geometry without leaving
  // the step.
  const [isAssetDrawerOpen, setIsAssetDrawerOpen] = useState(false)
  const openAssetDrawer = useCallback(() => setIsAssetDrawerOpen(true), [])
  const closeAssetDrawer = useCallback(() => setIsAssetDrawerOpen(false), [])

  // Single Edit Asset Model drawer shared by BOTH return branches
  // (missing-inputs and main) -- previously duplicated verbatim, a drift
  // trap. Because the editor puts by the passed model's own id/tags, this
  // targets the ticket's snapshot for asset-scoped workflows.
  const assetModelDrawer = (
    <Drawer
      open={isAssetDrawerOpen}
      onClose={closeAssetDrawer}
      placement='right'
      width={720}
      title='Edit Asset Model'
      destroyOnClose>
      {assetModel && component && unit ? (
        <FfsAssetModelEditor
          assetModel={assetModel}
          component={component}
          unit={unit}
          refreshData={refreshTicketContext}
        />
      ) : (
        <Alert
          type='warning'
          showIcon
          message='Asset model not loaded for this ticket.'
          description='The ticket must have a unit with an asset model entry before the editor can open.'
        />
      )}
    </Drawer>
  )

  const provenance = useMemo(() => {
    // Every t_min / MAWP input is an asset-model value. `jsonField` is the
    // stable asset-model JSON key; `label` is the human tooltip. The shared
    // hook resolves the citation (`source`) and the editable `onSourceChange`
    // from the ticket overlay; `onEdit` opens the in-step asset-model drawer.
    const assetModelProv = (
      jsonField: string,
      label: string
    ): InputProvenance => ({
      kind: 'assetModel',
      field: label,
      onEdit: openAssetDrawer,
      source: getFieldSource(jsonField),
    })
    return {
      D: assetModelProv('radius', 'componentModel.radius (*2)'),
      tNom: assetModelProv(
        'wall_thickness',
        'componentModel.wallNominalThickness'
      ),
      P: assetModelProv('design_pressure', 'design_pressure'),
      Sy: assetModelProv('yield_strength', 'yield_strength'),
      E: assetModelProv('joint_efficiency', 'joint_efficiency'),
      Y: assetModelProv('pressure_coefficient', 'pressure_coefficient'),
      tsl: assetModelProv('tsl', 'tsl'),
    }
  }, [getFieldSource, openAssetDrawer])

  const formulas = useMemo(() => buildMinThicknessFormulaJsx(raw), [raw])

  if (isLoading) {
    return (
      <Container>
        <Skeleton active />
      </Container>
    )
  }

  if (missingInputs.length > 0) {
    const hasAssetModelMissing = missingInputs.some((m) =>
      m.toLowerCase().includes('asset model')
    )
    const canOpenAssetDrawer = Boolean(assetModel && component && unit)
    return (
      <Container>
        <Typography.Title level={4} style={{ marginTop: 0 }}>
          Minimum Thickness Analysis
        </Typography.Title>
        <Alert
          type='error'
          showIcon
          message='Missing inputs from earlier steps.'
          description={
            <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
              {missingInputs.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          }
          action={
            hasAssetModelMissing && canOpenAssetDrawer ? (
              <Button size='small' type='primary' onClick={openAssetDrawer}>
                Edit Asset Model
              </Button>
            ) : null
          }
        />
        {assetModelDrawer}
      </Container>
    )
  }

  const tMinCNum = results.tMinC?.toNumber(WORKING_UNIT_THICKNESS) ?? null
  const tMinLNum = results.tMinL?.toNumber(WORKING_UNIT_THICKNESS) ?? null
  const tMinNum = results.tMin?.toNumber(WORKING_UNIT_THICKNESS) ?? null
  const mawpCNum = results.mawpC?.toNumber(WORKING_UNIT_PRESSURE) ?? null
  const mawpLNum = results.mawpL?.toNumber(WORKING_UNIT_PRESSURE) ?? null
  const mawpNum = results.mawp?.toNumber(WORKING_UNIT_PRESSURE) ?? null

  return (
    <Container>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        Minimum Thickness Analysis
      </Typography.Title>
      <Typography.Paragraph type='secondary' style={{ marginBottom: 0 }}>
        Component-level API 579 piping calculations: minimum required thickness
        (t<sub>min</sub>) for the design pressure, and maximum allowable working
        pressure (MAWP) at nominal thickness.
      </Typography.Paragraph>

      <SectionBlock>
        <SectionTitle>Inputs</SectionTitle>
        <InputProvenanceRow
          symbol={<>D</>}
          value={`${fmt(raw.D_mm)} mm`}
          provenance={provenance.D}
        />
        <InputProvenanceRow
          symbol={
            <>
              t<sub>nom</sub>
            </>
          }
          value={`${fmt(raw.tNom_mm)} mm`}
          provenance={provenance.tNom}
        />
        <InputProvenanceRow
          symbol={<>P</>}
          value={`${fmt(raw.P_MPa)} MPa`}
          provenance={provenance.P}
        />
        <InputProvenanceRow
          symbol={
            <>
              S<sub>y</sub>
            </>
          }
          value={`${fmt(raw.Sy_MPa)} MPa`}
          provenance={provenance.Sy}
        />
        <InputProvenanceRow
          symbol={<>E</>}
          value={fmt(raw.E)}
          provenance={provenance.E}
        />
        <InputProvenanceRow
          symbol={<>Y</>}
          value={fmt(raw.Y)}
          provenance={provenance.Y}
        />
        <InputProvenanceRow
          symbol={
            <>
              t<sub>sl</sub>
            </>
          }
          value={`${fmt(raw.tsl_mm)} mm`}
          provenance={provenance.tsl}
        />
      </SectionBlock>

      <SectionBlock>
        <SectionTitle>Minimum Thickness (t_min)</SectionTitle>
        {tMinCNum !== null ? (
          <FormulaDisplay
            label='Circumferential -- API 579 Annex 2C.5.3.1'
            symbolic={formulas.tMinC.symbolic}
            substituted={formulas.tMinC.substituted}
            result={{ value: tMinCNum, unit: 'mm' }}
          />
        ) : null}
        {tMinLNum !== null ? (
          <FormulaDisplay
            label='Longitudinal -- API 579 Annex 2C.5.3.2'
            symbolic={formulas.tMinL.symbolic}
            substituted={formulas.tMinL.substituted}
            result={{ value: tMinLNum, unit: 'mm' }}
          />
        ) : null}
        {tMinNum !== null ? (
          <FormulaDisplay
            label='Governing t_min = max(t_min^C, t_min^L)'
            symbolic={
              <>
                t<sub>min</sub> = max(t<sub>min</sub>
                <sup>C</sup>, t<sub>min</sub>
                <sup>L</sup>)
              </>
            }
            substituted={
              <>
                max({fmt(tMinCNum)}, {fmt(tMinLNum)})
              </>
            }
            result={{ value: tMinNum, unit: 'mm' }}
          />
        ) : null}
      </SectionBlock>

      <SectionBlock>
        <SectionTitle>Max Allowable Working Pressure (MAWP)</SectionTitle>
        {mawpCNum !== null ? (
          <FormulaDisplay
            label='Circumferential -- API 579 Annex 2C.5.3.1'
            symbolic={formulas.mawpC.symbolic}
            substituted={formulas.mawpC.substituted}
            result={{ value: mawpCNum, unit: 'MPa' }}
          />
        ) : null}
        {mawpLNum !== null ? (
          <FormulaDisplay
            label='Longitudinal -- API 579 Annex 2C.5.3.2'
            symbolic={formulas.mawpL.symbolic}
            substituted={formulas.mawpL.substituted}
            result={{ value: mawpLNum, unit: 'MPa' }}
          />
        ) : null}
        {mawpNum !== null ? (
          <FormulaDisplay
            label='Governing MAWP = min(MAWP^C, MAWP^L)'
            symbolic={
              <>
                MAWP = min(MAWP<sup>C</sup>, MAWP<sup>L</sup>)
              </>
            }
            substituted={
              <>
                min({fmt(mawpCNum)}, {fmt(mawpLNum)})
              </>
            }
            result={{ value: mawpNum, unit: 'MPa' }}
          />
        ) : null}
      </SectionBlock>

      <SectionBlock>
        <SectionTitle>Observed Minimum Thickness Check</SectionTitle>
        <ObservedMinThicknessCheckPanel
          tMinMm={tMinNumForCheck}
          observedMinMm={observedMinMm}
          pass={cdsCheckPass}
          isLoading={observedLoading}
          noCdf={noCdf}
        />
      </SectionBlock>

      {assetModelDrawer}
    </Container>
  )
}
